import Vue from 'vue';
import vcitaLogo from '@/assets/images/vcita-logo-white.svg';
import LayoutConstants from '@/constansts/layoutConstants';
import businessService from '@/modules/business/services/businessService';
import businessDateFormats from '@/modules/common/filters/BusinessDateFormats';
import businessContactDetailsStore from '@/modules/business/store/businessContactDetailsStore';
import businessSmsStore from '@/modules/business/store/businessSmsStore';
import eventListenerService from '@/utils/eventCommunicationUtils/eventListenerService';
import reviewSettingsService from '@/modules/reviews/services/reviewSettingsService';
import logError from '@/utils/logError/logError';
import { resolveHelpDestination, buildHelpLaunch } from '@/modules/help/utils/helpRules';

const state = {
  matterData: {
    matterTerm: 'clients',
    icon: 'icon-clients-POV',
    translation: 'common.navigation.clients',
  },
  branding: {
    colors: {
      primary: '#FFFFFF', // white so that the menu doesn't have a background color before brand is loaded
      secondary: '#0093B8',
      text: '#ffffff',
    },
    logo: null,
    poweredBy: {
      parent_logo: '',
      intandem_logo: '',
    },
  },
  isBrandingLoaded: false,
  features: [],
  featureFlagsLoaded: false,
  attributes: {},
  businessProfile: null,
  reviewSettings: null,
  analyticsConfig: {
    disableAnalytics: null,
  },
};

const helpers = {
  /**
   * Get forced feature flags from URL and localStorage (non-production only)
   *
   * URL: ?forceFeatureFlagName=FEATURE_NAME (same as Angular, single flag)
   * localStorage: addFeatureFlags = ["flag1", "flag2"] (array of flags, persists across reloads)
   *
   * @returns {string[]} Array of forced feature flag names
   */
  getForcedFeatureFlags: () => {
    const isProduction = window.pov_config?.VUE_APP_ENV === 'production';
    if (isProduction) return [];

    const forcedFlags = [];

    // Check URL param (single flag, same as Angular)
    const urlParams = new URLSearchParams(window.location.search);
    const urlFlag = urlParams.get('forceFeatureFlagName');
    if (urlFlag) {
      forcedFlags.push(urlFlag);
    }

    // Check localStorage (array of flags, persists across reloads)
    try {
      const storedFlags = localStorage.getItem('addFeatureFlags');
      if (storedFlags) {
        const parsed = JSON.parse(storedFlags);
        if (Array.isArray(parsed)) {
          forcedFlags.push(...parsed);
        }
      }
    } catch (e) {
      console.warn('[DEV] Failed to parse addFeatureFlags from localStorage:', e);
    }

    return [...new Set(forcedFlags)]; // Remove duplicates
  },

  mapBrandingResponse: (branding, rootGetters) => {
    const poweredByMapped = {};
    const isMobile = rootGetters['CommonStore/isMobile'];

    if (isMobile || helpers.isPrimaryColorWhite(branding.colors.primary)) {
      poweredByMapped.parent_logo = branding.powered_by?.parent_logo_dark || '';
      poweredByMapped.intandem_logo = branding.powered_by?.intandem_logo_dark || '';
    } else {
      poweredByMapped.parent_logo = branding.powered_by?.parent_logo_bright || '';
      poweredByMapped.intandem_logo = branding.powered_by?.intandem_logo_bright || '';
    }
    const brandingObject = { colors: branding.colors, logo: branding.logo || vcitaLogo, poweredBy: poweredByMapped };
    return brandingObject;
  },
  isPrimaryColorWhite: (primaryColor) => ['#ffffff', '#fff', '#ffffffff', 'rgb(255, 255, 255)'].includes(primaryColor.toLowerCase()),
  supportArticleUrl: (articleCode, attributes, brandedZendeskEnabled) => {
    if (brandedZendeskEnabled && attributes?.directory_settings?.faq_url) {
      const url = new URL(attributes.directory_settings.faq_url);
      url.searchParams.append('article_code', articleCode);

      return url.toString();
    }
    return '';
  },
  brandedZendeskSupportArticleUrl: (articleCode, attributes, rootGetters) => {
    const brandedZendeskEnabled = rootGetters['BusinessStore/hasFeature']('multi_branded_zendesk');
    return helpers.supportArticleUrl(articleCode, attributes, brandedZendeskEnabled);
  },
  brandedSupportArticleUrl: (articleCode, attributes, rootGetters, {
    partnerDirectoryUrl = '',
    directUrl = '',
  } = {}) => {
    const zendeskUrl = helpers.brandedZendeskSupportArticleUrl(articleCode, attributes, rootGetters);
    if (zendeskUrl) {
      return zendeskUrl;
    }
    if (partnerDirectoryUrl && rootGetters['BusinessStore/isPartnerDirectory']) {
      return partnerDirectoryUrl;
    }
    if (directUrl) {
      return directUrl;
    }
    return null;
  },
  buildMatterData: (matterTerm) => {
    const translationKey = LayoutConstants.MATTER_TRANSLATIONS_KEYS[matterTerm];
    const matterData = {
      matterTerm,
      icon: `icon-${matterTerm.toLowerCase()}-POV`,
      translation: translationKey,
    };
    return matterData;
  },
  buildAvailableLocales: (currentLocale, hasCustomLocalesFeature, isFree) => {
    const baseLocales = [
      { key: 'en', title: 'English (US)' },
      { key: 'es', title: 'Español' },
      { key: 'fr', title: 'Français' },
      { key: 'it', title: 'Italiano' },
      { key: 'pt', title: 'Português' },
      { key: 'de', title: 'Deutsch' },
      { key: 'pl', title: 'Polski' },
      { key: 'nl', title: 'Nederlands' },
      { key: 'en-GB', title: 'English (UK)' },
      { key: 'lv', title: 'Latviešu' },
    ];

    const conditionalLocales = [];

    if (currentLocale === 'he' || !isFree) {
      conditionalLocales.push({ key: 'he', title: 'עברית' });
    }

    if (currentLocale === 'sl') {
      conditionalLocales.push({ key: 'sl', title: 'Slovenščina' });
    }

    if (hasCustomLocalesFeature) {
      conditionalLocales.push({ key: 'keys', title: 'Exposed keys' });
      conditionalLocales.push({ key: 'pseudo', title: 'Pseudo translations' });
    }

    return [...baseLocales, ...conditionalLocales];
  },
};

const mutations = {
  setMatterData(state, matterData) {
    state.matterData = matterData;
  },
  setBranding(state, branding) {
    state.branding = branding;
    state.isBrandingLoaded = true;
  },
  setAttribute(state, payload) {
    state.attributes = { ...state.attributes, [payload.key]: payload.value };
  },
  setAttributes(state, attributes) {
    state.attributes = attributes;
  },
  setFeatureFlags(state, features) {
    state.features = features;
  },
  setFeatureFlagsLoaded(state, loaded) {
    state.featureFlagsLoaded = loaded;
  },
  setReviewSettings(state, settings) {
    state.reviewSettings = settings;
  },
  setBusinessProfile(state, profile) {
    state.businessProfile = profile;
  },
  setAnalyticsConfig(state, config) {
    state.analyticsConfig = { ...state.analyticsConfig, ...config };
  },
};

const actions = {
  async fetchBranding({ commit, rootGetters }) {
    try {
      const branding = await businessService.fetchBranding();
      if (branding) {
        const mappedBranding = helpers.mapBrandingResponse(branding, rootGetters);
        commit('setBranding', mappedBranding);
      }
    } catch (e) {
      console.error('error', e);
    }
  },

  async fetchAttributes({ commit, dispatch, rootGetters }) {
    try {
      const attributes = await businessService.fetchAttributes();
      commit('setAttributes', attributes);
      // eslint-disable-next-line camelcase
      const { locale, time_format } = attributes;
      Vue.prototype.$ds.setLocale(locale || 'en');
      Vue.prototype.setLocale(locale || 'en');
      Vue.prototype.addI18nModule('common-pov');
      businessDateFormats.setLocale(locale, time_format);

      window.supportArticleUrl = (
        articleCode,
        {
          partnerDirectoryUrl = '',
          directUrl = '',
        } = {},
      ) => helpers.brandedSupportArticleUrl(articleCode, attributes, rootGetters, {
        partnerDirectoryUrl,
        directUrl,
      });

      window.openSupportArticle = (articleCode, {
        partnerDirectoryUrl = '',
        directUrl = '',
        message = null,
        source = 'help_article',
      } = {}) => {
        const destination = resolveHelpDestination(rootGetters);
        if (destination === 'none') return;
        if (destination === 'chat') {
          dispatch('AiChatStore/launchWorkflow', buildHelpLaunch({ message, source }), { root: true });
          return;
        }
        const url = window.supportArticleUrl(articleCode, {
          partnerDirectoryUrl,
          directUrl,
        });
        if (url) {
          window.open(`${url.toString()}`, '_blank');
        }
      };

      const matterTerm = attributes.matter_term;
      if (matterTerm) {
        commit('setMatterData', helpers.buildMatterData(matterTerm));
      }
    } catch (e) {
      console.error('error', e);
    }
  },
  setAttribute({ commit }, payload) {
    commit('setAttribute', payload);
    if (payload.key === 'time_format') {
      businessDateFormats.setLocale(null, payload.value);
    }
  },

  async fetchFeatureFlags({ rootGetters, commit }) {
    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      let features = await businessService.fetchFeaturesFlags(businessUid) || [];

      // Add forced feature flags from URL and localStorage (non-production only)
      const forcedFlags = helpers.getForcedFeatureFlags();
      if (forcedFlags.length > 0) {
        const newFlags = forcedFlags.filter((flag) => !features.includes(flag));
        if (newFlags.length > 0) {
          features = [...features, ...newFlags];
          console.info(`[DEV] Force enabled feature flags: ${newFlags.join(', ')}`);
        }
      }

      const loaded = features?.length > 0;
      commit('setFeatureFlagsLoaded', loaded);
      commit('setFeatureFlags', features);
      if (loaded) {
        eventListenerService.emit('featureFlagsLoaded');
      }
    } catch (e) {
      console.error('error', e);
    }
  },
  async getReviewSettings({ rootGetters, commit }) {
    const res = {};

    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      const settings = await reviewSettingsService.getReviewSettings(businessUid);
      commit('setReviewSettings', settings);
    } catch (e) {
      console.error('error', e);
      res.error = e;
    }

    return res;
  },
  setAnalyticsConfig({ commit }, config) {
    commit('setAnalyticsConfig', config);
  },
  async setReviewSettings({ rootGetters, state, commit }, settings) {
    const res = {};

    try {
      const businessUid = rootGetters['AuthStore/businessUid'];
      let newSettings;

      if (state.reviewSettings) {
        newSettings = await reviewSettingsService.updateReviewSettings(businessUid, settings);
      } else {
        newSettings = await reviewSettingsService.createReviewSettings(settings);
      }

      commit('setReviewSettings', newSettings);
    } catch (e) {
      console.error('error', e);
      res.error = e;
    }

    return res;
  },
  async fetchBusinessProfile({ state, commit }, { force = false } = {}) {
    if (state.businessProfile && !force) return;
    try {
      const profile = await businessService.fetchBusinessProfile();
      commit('setBusinessProfile', profile);
    } catch (e) {
      logError('Error fetching business profile', e);
    }
  },
};

const getters = {
  matterTerm: (state) => state.matterData.matterTerm,
  matterTermIcon: (state) => state.matterData.icon,
  matterTermTranslation: (state) => state.matterData.translation,
  primaryColor: (state) => state.branding.colors.primary,
  brandColor: (state) => state.branding.colors.brand_color || state.branding.colors.primary,
  businessSettingsBrandColor: (state) => state.branding.colors.brand_color,
  businessLogoUrl: (state) => state.branding.logo,
  businessSettingsTextColor: (state) => state.branding.colors.text,
  secondaryColor: (state) => state.branding.colors.secondary,
  textColor: (state) => state.branding.colors.text,
  isBrandingLoaded: (state) => state.isBrandingLoaded,
  logo: (state) => state.branding.logo,
  parentPoweredByLogo: (state) => state.branding.poweredBy.parent_logo,
  inTandemPoweredByLogo: () => state.branding.poweredBy.intandem_logo,
  attributes: (state) => state.attributes,
  brandName: (state) => state.attributes?.brand_name,
  featureFlags: (state) => state.features,
  featureFlagsLoaded: (state) => state.featureFlagsLoaded,
  hasFeature: (state) => (fName) => state.features.includes(fName),
  businessProfile: (state) => state.businessProfile,
  livesiteUrl: (state) => state.businessProfile?.livesite_url,
  appHost: (state) => state.attributes.app_host,
  directorySettings: (state) => state.attributes?.directory_settings || {},
  isPartnerDirectory: (state) => (
    !!state.attributes?.directory_settings?.is_whitelabel || !!state.attributes?.directory_settings?.is_co_branded_managed
  ),
  getDirectoryLogoutUrl: (state) => (
    state.attributes?.directory_settings?.logout_url
  ),
  hasHelpLinks: (state) => !!state.attributes?.directory_settings?.custom_support_url
    || !!state.attributes?.directory_settings?.faq_url
    || !!state.attributes?.directory_settings?.help_links,
  supportArticleUrl: (state) => (articleCode) => helpers.supportArticleUrl(articleCode, state.attributes, getters.hasFeature(state)('multi_branded_zendesk')),
  isSpecialDirectory: (state) => (
    state.attributes?.directory_settings?.is_whitelabel || state.attributes?.directory_settings?.is_co_branded_managed || false
  ),
  // TODO: test the isBranded getter, is it the same as before?
  // isBranded: state => state.branding && (state.branding.whitelabel || state.branding.co_branded_managed), <- frontge-vue
  isBranded: (state) => !!(state.attributes?.directory_settings?.is_whitelabel
    || state.attributes?.directory_settings?.is_co_branded_managed
    || state.attributes?.directory_settings?.is_managed_partner),
  locale: (state) => state.attributes.locale || 'en',
  reviewSettings: (state) => state.reviewSettings,
  analyticsConfig: (state) => state.analyticsConfig,
  isAnalyticsConfigReady: (state) => state.analyticsConfig.disableAnalytics !== null,
  isAnalyticsDisabled: (state) => state.analyticsConfig.disableAnalytics !== false,
  availableLocales: (state) => helpers.buildAvailableLocales(
    getters.locale(state),
    getters.hasFeature(state)('custom_locales'),
    state.attributes?.is_free,
  ),
};

const modules = {
  BusinessContactDetailsStore: businessContactDetailsStore,
  businessSms: businessSmsStore,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules,
};
